import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MessageCircle, Send, X, Phone, User, Clock, Bell, BellRing, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminLiveChat = () => {
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [unreadCount, setUnreadCount] = useState({ waiting: 0, unread: 0 });
  const [soundEnabled, setSoundEnabled] = useState(true);
  const messagesEndRef = useRef(null);
  const audioRef = useRef(null);
  const prevWaitingCount = useRef(0);

  const getAuthHeader = () => {
    const creds = localStorage.getItem('adminAuth');
    return creds ? `Basic ${creds}` : '';
  };

  const fetchSessions = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/live-chats`, {
        headers: { 'Authorization': getAuthHeader() }
      });
      if (res.ok) {
        const data = await res.json();
        setSessions(data.sessions || []);
      }
    } catch (err) {
      console.error('Failed to fetch sessions:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/live-chats/unread-count`, {
        headers: { 'Authorization': getAuthHeader() }
      });
      if (res.ok) {
        const data = await res.json();
        
        // Play sound if new waiting session
        if (data.waiting > prevWaitingCount.current && soundEnabled) {
          playNotificationSound();
        }
        prevWaitingCount.current = data.waiting;
        
        setUnreadCount(data);
      }
    } catch (err) {
      console.error('Failed to fetch unread count:', err);
    }
  }, [soundEnabled]);

  const playNotificationSound = () => {
    if (audioRef.current) {
      audioRef.current.play().catch(e => console.log('Audio play failed:', e));
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchSessions();
    fetchUnreadCount();
  }, [fetchSessions, fetchUnreadCount]);

  // Poll for updates every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchSessions();
      fetchUnreadCount();
      
      // If a session is selected, refresh it
      if (selectedSession) {
        fetchSessionDetails(selectedSession.session_id);
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [fetchSessions, fetchUnreadCount, selectedSession]);

  const fetchSessionDetails = async (sessionId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/live-chat/${sessionId}`, {
        headers: { 'Authorization': getAuthHeader() }
      });
      if (res.ok) {
        const data = await res.json();
        setSelectedSession(data);
      }
    } catch (err) {
      console.error('Failed to fetch session details:', err);
    }
  };

  const handleSelectSession = async (session) => {
    await fetchSessionDetails(session.session_id);
  };

  const handleSendReply = async () => {
    if (!replyMessage.trim() || !selectedSession || isSending) return;

    setIsSending(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/live-chat/${selectedSession.session_id}/reply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': getAuthHeader()
        },
        body: JSON.stringify({ message: replyMessage.trim() })
      });

      if (res.ok) {
        setReplyMessage('');
        await fetchSessionDetails(selectedSession.session_id);
        toast.success('Message sent');
      } else {
        toast.error('Failed to send message');
      }
    } catch (err) {
      toast.error('Failed to send message');
    } finally {
      setIsSending(false);
    }
  };

  const handleCloseChat = async () => {
    if (!selectedSession) return;

    try {
      const res = await fetch(`${API_URL}/api/admin/live-chat/${selectedSession.session_id}/close`, {
        method: 'POST',
        headers: { 'Authorization': getAuthHeader() }
      });

      if (res.ok) {
        toast.success('Chat closed');
        setSelectedSession(null);
        fetchSessions();
      }
    } catch (err) {
      toast.error('Failed to close chat');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendReply();
    }
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [selectedSession?.messages]);

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="h-[calc(100vh-8rem)] flex flex-col">
      {/* Hidden audio element for notifications */}
      <audio ref={audioRef} preload="auto">
        <source src="data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2telegsliOT+u3UZLY3m/rlxGSyJ5f65cBksi+b+uXAZLIvm/rlwGS2N5v+5cRktiub/uHAZLYvm/7lxGS2K5v+5cBktiuX/uXAZLYrl/7lwGS2K5f+5cBktiuX/uXAZLIrl/7hvGSyK5P+4bxksiuT/uG8ZLInk/rhvGSyJ4/64bxkrieP+uG4ZK4ni/rhtGSuJ4v64bRkriOL+t20ZKoji/bdtGSmI4f23bBkpiOH9t2wZKYfh/bdrGSmH4P23axkph+D9t2sZKIbg/bZrGSiG3/22ahkoht/9tmoZKIXf/bVqGSeF3v21aRknhd79tWkZJ4Td/bRpGSaE3f20aBkmg9z8tGgZJoPc/LRnGSaD2/yzZxklgtr8s2YZJYLa/LNmGSWB2fyy" type="audio/wav" />
      </audio>

      {/* Header */}
      <div className="bg-white p-4 border-b flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full flex items-center justify-center text-white">
            <MessageCircle className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-800">Live Chat Support</h1>
            <p className="text-sm text-gray-500">Respond to customer inquiries in real-time</p>
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          {unreadCount.waiting > 0 && (
            <div className="flex items-center gap-2 bg-red-100 text-red-700 px-3 py-2 rounded-lg animate-pulse">
              <BellRing className="w-5 h-5" />
              <span className="font-medium">{unreadCount.waiting} waiting</span>
            </div>
          )}
          
          <button
            onClick={() => setSoundEnabled(!soundEnabled)}
            className={`p-2 rounded-lg transition-colors ${soundEnabled ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-400'}`}
            title={soundEnabled ? 'Sound on' : 'Sound off'}
          >
            {soundEnabled ? <Bell className="w-5 h-5" /> : <Bell className="w-5 h-5" />}
          </button>
          
          <button
            onClick={() => { fetchSessions(); fetchUnreadCount(); }}
            className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            title="Refresh"
          >
            <RefreshCw className="w-5 h-5 text-gray-600" />
          </button>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Sessions List */}
        <div className="w-80 border-r bg-gray-50 overflow-y-auto">
          {isLoading ? (
            <div className="p-4 text-center text-gray-500">Loading...</div>
          ) : sessions.length === 0 ? (
            <div className="p-8 text-center">
              <MessageCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No active chats</p>
              <p className="text-sm text-gray-400 mt-1">Waiting for customers...</p>
            </div>
          ) : (
            sessions.map((session) => (
              <div
                key={session.session_id}
                onClick={() => handleSelectSession(session)}
                className={`p-4 border-b cursor-pointer transition-colors ${
                  selectedSession?.session_id === session.session_id
                    ? 'bg-blue-50 border-l-4 border-l-blue-500'
                    : 'hover:bg-gray-100'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      session.status === 'waiting' ? 'bg-amber-100 text-amber-600' : 'bg-green-100 text-green-600'
                    }`}>
                      <User className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">{session.customer_name}</p>
                      <p className="text-xs text-gray-500 flex items-center gap-1">
                        <Phone className="w-3 h-3" />
                        {session.customer_phone}
                      </p>
                    </div>
                  </div>
                  
                  {session.status === 'waiting' && (
                    <span className="px-2 py-1 text-xs bg-amber-100 text-amber-700 rounded-full">
                      New
                    </span>
                  )}
                  {session.unread_count > 0 && session.status !== 'waiting' && (
                    <span className="px-2 py-1 text-xs bg-red-500 text-white rounded-full">
                      {session.unread_count}
                    </span>
                  )}
                </div>
                
                <div className="mt-2 text-xs text-gray-400 flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {formatDate(session.last_activity)}
                </div>
                
                {session.messages?.length > 0 && (
                  <p className="mt-2 text-sm text-gray-500 truncate">
                    {session.messages[session.messages.length - 1]?.content}
                  </p>
                )}
              </div>
            ))
          )}
        </div>

        {/* Chat Area */}
        <div className="flex-1 flex flex-col bg-white">
          {selectedSession ? (
            <>
              {/* Chat Header */}
              <div className="p-4 border-b flex items-center justify-between bg-gradient-to-r from-blue-50 to-indigo-50">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full flex items-center justify-center text-white font-bold">
                    {selectedSession.customer_name?.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800">{selectedSession.customer_name}</p>
                    <p className="text-sm text-gray-500 flex items-center gap-2">
                      <Phone className="w-4 h-4" />
                      {selectedSession.customer_phone}
                    </p>
                  </div>
                </div>
                
                <Button
                  onClick={handleCloseChat}
                  variant="outline"
                  className="text-red-600 border-red-200 hover:bg-red-50"
                >
                  <X className="w-4 h-4 mr-2" />
                  Close Chat
                </Button>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
                {selectedSession.messages?.map((msg, idx) => (
                  <div
                    key={idx}
                    className={`flex ${msg.role === 'agent' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[70%] p-3 rounded-2xl ${
                        msg.role === 'agent'
                          ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-br-md'
                          : 'bg-white text-gray-800 shadow-sm border border-gray-200 rounded-bl-md'
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                      <p className={`text-xs mt-1 ${msg.role === 'agent' ? 'text-blue-100' : 'text-gray-400'}`}>
                        {formatTime(msg.timestamp)}
                      </p>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              {/* Reply Input */}
              <div className="p-4 bg-white border-t">
                <div className="flex items-end gap-2">
                  <textarea
                    value={replyMessage}
                    onChange={(e) => setReplyMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Type your reply..."
                    className="flex-1 resize-none border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent max-h-24"
                    rows={1}
                  />
                  <Button
                    onClick={handleSendReply}
                    disabled={!replyMessage.trim() || isSending}
                    className="bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white p-3 rounded-xl"
                  >
                    <Send className="w-5 h-5" />
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center bg-gray-50">
              <div className="text-center">
                <MessageCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-600">Select a conversation</h3>
                <p className="text-sm text-gray-400 mt-1">Choose a chat from the list to start responding</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminLiveChat;
